import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import LoadingState from '@/components/LoadingState';
import { quickBooksPendingAuthKey } from '@/hooks/useQuickBooks';

/** The organisation saved by connectToQuickBooks, if the returned state matches one it issued. */
async function pendingOrganisationId(state: string | null): Promise<string | null> {
  if (!state) return null;
  try {
    return localStorage.getItem(await quickBooksPendingAuthKey(state)) || null;
  } catch {
    return null;
  }
}

export default function QuickBooksCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const realmId = searchParams.get('realmId');
      const error = searchParams.get('error');
      const state = searchParams.get('state');
      const organisationId = await pendingOrganisationId(state);

      if (error) {
        toast.error(`QuickBooks connection failed: ${error}`);
        navigate('/');
        return;
      }

      if (!code || !realmId) {
        toast.error('Invalid QuickBooks callback - missing parameters');
        navigate('/');
        return;
      }

      if (!state || !organisationId) {
        toast.error('QuickBooks connection could not be verified. Please try again.');
        navigate('/');
        return;
      }

      try {
        // Use Supabase client to invoke the edge function
        const { data, error } = await invokeFunction(supabase, 'quickbooks-oauth', {
          body: {
            action: 'callback',
            code,
            realmId,
            organisationId,
          }
        });

        if (error) {
          throw new Error(error.message || 'Failed to complete OAuth flow');
        }

        localStorage.removeItem(await quickBooksPendingAuthKey(state));
        toast.success('Successfully connected to QuickBooks!');
        
        // Close the popup window if this was opened in a popup
        if (window.opener) {
          window.close();
        } else {
          // Navigate back to the organization page
          navigate('/');
        }
        
      } catch (error) {
        console.error('QuickBooks callback error:', error);
        toast.error('Failed to complete QuickBooks connection');
        navigate('/');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <LoadingState />
        <p className="mt-4 text-muted-foreground">
          Completing QuickBooks connection...
        </p>
      </div>
    </div>
  );
}